/**
 * SOC Settings — reads company-level and user-level configuration
 * sent by the parent SOC application.
 *
 * Parameters read:
 *   userAutocomplete             (boolean, default true) — replaces legacy autocomplete
 *   companyChatStorage           (boolean, default true)
 *   companyChatRetentionDays     (integer, default null = unlimited)
 *   companyPermitUserProfiles    (boolean, default true)
 *   userEnableUserProfile        (boolean, default false)
 *   userPrecedentCasesPreview    (boolean, default false)
 *   userPrecedentCasesPaid       (boolean, default false)
 *
 * Backward compatibility:
 *   autocomplete is still honoured as a fallback for userAutocomplete during the
 *   transition period. userAutocomplete takes precedence when both are present.
 *   This fallback should be removed once the SOC app fully migrates.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SocSettings {
  /** User-level autocomplete preference. */
  userAutocomplete: boolean;
  /** Company-level: whether chats should be stored. */
  companyChatStorage: boolean;
  /** Company-level: chat retention period in days (null = no limit). */
  companyChatRetentionDays: number | null;
  /** Company-level: whether User Profiles are permitted. */
  companyPermitUserProfiles: boolean;
  /** User-level: whether the user has enabled their own profile. */
  userEnableUserProfile: boolean;
  /** User-level: whether the user has preview access to Precedent Cases. */
  userPrecedentCasesPreview: boolean;
  /** User-level: whether the user has paid access to Precedent Cases. */
  userPrecedentCasesPaid: boolean;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const SOC_SETTINGS_DEFAULTS: SocSettings = {
  userAutocomplete: true,
  companyChatStorage: true,
  companyChatRetentionDays: null,
  companyPermitUserProfiles: true,
  userEnableUserProfile: false,
  userPrecedentCasesPreview: false,
  userPrecedentCasesPaid: false,
};

// ---------------------------------------------------------------------------
// Parsing helpers
// ---------------------------------------------------------------------------

/**
 * Parse a boolean-like message value.
 * Only exact "true" / "false" or "t" / "f" (case-insensitive) are honoured.
 * Missing, empty, or any other string returns `defaultValue`.
 */
export function parseBoolAttr(value: string | null, defaultValue: boolean): boolean {
  if (value === null) return defaultValue;
  const normalised = value.trim().toLowerCase();
  if (normalised === 'true' || normalised === 't') return true;
  if (normalised === 'false' || normalised === 'f') return false;
  return defaultValue;
}

function hasOwn(data: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(data, key);
}

function messageValueToString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  return null;
}

function parseIntMessageAttr(value: unknown, previous: number | null): number | null {
  const raw = messageValueToString(value);
  if (raw === null) return previous;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = parseInt(trimmed, 10);
  if (Number.isNaN(parsed)) return previous;
  if (parsed <= 0) return null;
  return parsed;
}

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/**
 * A User Profile is effectively enabled only when BOTH conditions are met:
 *   1. The company permits user profiles.
 *   2. The user has opted in.
 */
export function isUserProfileEffectivelyEnabled(settings: SocSettings): boolean {
  return settings.companyPermitUserProfiles && settings.userEnableUserProfile;
}

/**
 * Merge SOC settings from a postMessage payload sent by the parent SOC app.
 * Missing or malformed values preserve the previous setting.
 */
export function mergeSocSettingsFromMessage(
  data: Record<string, unknown>,
  previous: SocSettings = SOC_SETTINGS_DEFAULTS,
): SocSettings {
  const userAutocompleteRaw = hasOwn(data, 'userAutocomplete')
    ? messageValueToString(data.userAutocomplete)
    : messageValueToString(data.autocomplete);

  return {
    userAutocomplete: hasOwn(data, 'userAutocomplete') || hasOwn(data, 'autocomplete')
      ? parseBoolAttr(userAutocompleteRaw, previous.userAutocomplete)
      : previous.userAutocomplete,
    companyChatStorage: hasOwn(data, 'companyChatStorage')
      ? parseBoolAttr(messageValueToString(data.companyChatStorage), previous.companyChatStorage)
      : previous.companyChatStorage,
    companyChatRetentionDays: hasOwn(data, 'companyChatRetentionDays')
      ? parseIntMessageAttr(data.companyChatRetentionDays, previous.companyChatRetentionDays)
      : previous.companyChatRetentionDays,
    companyPermitUserProfiles: hasOwn(data, 'companyPermitUserProfiles')
      ? parseBoolAttr(messageValueToString(data.companyPermitUserProfiles), previous.companyPermitUserProfiles)
      : previous.companyPermitUserProfiles,
    userEnableUserProfile: hasOwn(data, 'userEnableUserProfile')
      ? parseBoolAttr(messageValueToString(data.userEnableUserProfile), previous.userEnableUserProfile)
      : previous.userEnableUserProfile,
    userPrecedentCasesPreview: hasOwn(data, 'userPrecedentCasesPreview')
      ? parseBoolAttr(messageValueToString(data.userPrecedentCasesPreview), previous.userPrecedentCasesPreview)
      : previous.userPrecedentCasesPreview,
    userPrecedentCasesPaid: hasOwn(data, 'userPrecedentCasesPaid')
      ? parseBoolAttr(messageValueToString(data.userPrecedentCasesPaid), previous.userPrecedentCasesPaid)
      : previous.userPrecedentCasesPaid,
  };
}
